import type { ChartAudio, ChartPalette } from '../chart.js';
import { buildVariantTitle } from '../chart.js';
import {
  GOOD_WINDOW_MS,
  PERFECT_WINDOW_MS,
  VARIANTS,
  buildVariantSlug,
  type Difficulty,
  type Variant,
} from '../gameTypes.js';
import { computeFillerBeats, fillerPhase, type FillerSpan } from '../fillerNotes.js';
import type { GuitarChart } from '../guitarChart.js';
import { MANIA_LANE_COUNT, type ManiaLane, type ManiaNote } from '../maniaNote.js';

/**
 * Wire format for one variant of an osu!mania-imported Guitar chart.
 * Mirrors `ImportedCircleBundle` but mania-shaped: a flat note array
 * keyed on `(beat, lane)` rather than osu! `(x, y)` hit objects.
 *
 * Persisted on disk as
 * `src/shared/builtinGuitarMaps/imported/<slug>-<variant>.guitar.json`
 * by `tools/RebuildGuitarFromOsu.ts`.
 *
 * `lane` and `dualPartnerLane` are typed as `number` here because TS
 * widens JSON imports — the writer only emits `0..3`, but we still
 * narrow at the boundary in {@link buildNotesFromImported} so a
 * future hand-edit can't sneak invalid lanes past the engine.
 */
export interface ImportedGuitarNoteEntry {
  id?: string;
  beat: number;
  lane: number;
  dualPartnerLane?: number;
  holdEndBeat?: number;
}

export interface ImportedGuitarBundle {
  slug: string;
  variant: string;
  bpm: number;
  /** First-uninherited-timing-point offset in ms (≥ 0 after the audio cut). */
  offsetMs: number;
  totalBeats: number;
  /** Length of the shipped audio file in seconds. */
  durationSec: number;
  /** Mapper credit (set host plus any guest mappers, " & "-joined). */
  mapper: string;
  /** Source `Version` label from the .osu file (e.g. `[4K Lv.13]`). Informational. */
  sourceDifficulty: string;
  notes: ReadonlyArray<ImportedGuitarNoteEntry>;
}

/**
 * Per-variant tuning for Guitar mode. The same three knobs Piano uses
 * (`approachMs` × `windowScale`) tuned for Guitar's denser osu!mania
 * sources. Normal is more forgiving than Piano (1450 ms / 1.05) so
 * beginners can read lanes; Hard / Extreme step down aggressively
 * (900 / 0.78 → 550 / 0.55). Compare to Piano's 1300 / 920 / 700
 * × 1.0 / 0.85 / 0.7.
 *
 * Lives next to the chart wrapper rather than in `guitarChart.ts`
 * because the constants are tuning rather than schema — the validator
 * doesn't depend on these values, the chart-assembly helper does.
 */
export const GUITAR_VARIANT_TUNING: Record<
  Variant,
  { approachMs: number; windowScale: number }
> = {
  // Normal is deliberately more forgiving than Piano (1300 ms / 1.0):
  // osu!mania source charts are denser, but beginners still need room
  // to read lanes before timing pressure kicks in. Hard / extreme step
  // down aggressively so variant choice is felt in the hands, not just
  // the timing-window labels.
  normal: { approachMs: 1450, windowScale: 1.05 },
  hard: { approachMs: 900, windowScale: 0.78 },
  extreme: { approachMs: 550, windowScale: 0.55 },
};

function isManiaLane(value: number): value is ManiaLane {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

/**
 * Narrow the imported note entries onto the typed {@link ManiaNote}
 * shape. Skips rows whose lane (or dual-partner lane) is out of
 * range — the writer only emits 0..3, but we guard here so a
 * hand-edited JSON or a future writer bug can't poison the engine.
 */
function buildNotesFromImported(
  entries: ReadonlyArray<ImportedGuitarNoteEntry>,
): ManiaNote[] {
  const out: ManiaNote[] = [];
  for (let i = 0; i < entries.length; i += 1) {
    const e = entries[i];
    if (!isManiaLane(e.lane)) {
      console.warn(
        `[importedGuitarChart] dropping note ${e.id ?? i} with invalid lane ${e.lane}`,
      );
      continue;
    }
    const note: ManiaNote = {
      id: e.id ?? `n-${i.toString().padStart(6, '0')}`,
      beat: e.beat,
      lane: e.lane,
    };
    if (e.dualPartnerLane !== undefined) {
      if (!isManiaLane(e.dualPartnerLane) || e.dualPartnerLane === e.lane) {
        // Drop bad partner without dropping the primary, but log
        // loudly: the importer is supposed to enforce
        // `dualPartnerLane !== lane` and the lane-range invariant
        // before serialising. Hitting this branch means a
        // hand-edited JSON or a future writer regression.
        console.warn(
          `[importedGuitarChart] note ${note.id} has invalid dualPartnerLane ${e.dualPartnerLane} (lane=${e.lane}); dropping partner`,
        );
      } else {
        note.dualPartnerLane = e.dualPartnerLane;
      }
    }
    if (e.holdEndBeat !== undefined) {
      if (e.holdEndBeat > e.beat) {
        note.holdEndBeat = e.holdEndBeat;
      } else {
        // Same rationale as the partner-drop above. The importer
        // upstream rejects zero-length holds, so a degenerate value
        // here means data drift.
        console.warn(
          `[importedGuitarChart] note ${note.id} has invalid holdEndBeat ${e.holdEndBeat} <= beat ${e.beat}; demoting to tap`,
        );
      }
    }
    out.push(note);
  }
  return out;
}

/**
 * Append on-the-beat filler notes to a mania note list, backfilling any
 * genuinely-long empty gap (see {@link computeFillerBeats}). Lanes climb
 * 0→1→2→3 across each phrase's four on-beats — a plain ascending run
 * that's trivially playable and never stacks two notes on one beat. A
 * note's hold span counts as occupied, so filler is never placed inside
 * a long hold that merely looks like a gap.
 */
export function withFillerGuitarNotes(
  notes: ReadonlyArray<ManiaNote>,
  totalBeats: number,
): ManiaNote[] {
  const occupied: FillerSpan[] = notes.map((n) => ({
    startBeat: n.beat,
    endBeat: n.holdEndBeat ?? n.beat,
  }));
  const fillerBeats = computeFillerBeats(occupied);
  if (fillerBeats.length === 0) {
    return [...notes];
  }
  const filler: ManiaNote[] = [];
  for (let i = 0; i < fillerBeats.length; i += 1) {
    const beat = fillerBeats[i];
    if (beat > totalBeats) {
      continue;
    }
    // `phase` is 0..onBeats-1, and onBeats (4) === MANIA_LANE_COUNT, so the
    // lane climbs 0→1→2→3 across a phrase and no two filler notes in the
    // same phrase ever share a lane. The `%` only bites if that invariant
    // is ever broken — lanes would repeat rather than overflow the count.
    const phase = fillerPhase(beat);
    filler.push({
      id: `f-${i.toString().padStart(6, '0')}`,
      beat,
      lane: (phase % MANIA_LANE_COUNT) as ManiaLane,
    });
  }
  return [...notes, ...filler].sort((a, b) => a.beat - b.beat || a.id.localeCompare(b.id));
}

export interface AssembleImportedGuitarArgs {
  baseSlug: string;
  baseTitle: string;
  description: string;
  difficulty: Difficulty;
  palette: ChartPalette;
  audioSrc: string;
  attribution: string;
  imported: Record<Variant, ImportedGuitarBundle>;
}

/**
 * Build all three GuitarChart variants for an osu!mania-imported
 * track. Mirrors `assembleImportedCirclesChart` in shape so adding a
 * new mode follows a parallel pattern.
 *
 * Per-variant `bpm` / `offsetMs` / `totalBeats` come from each
 * imported bundle (the first uninherited timing point in the source
 * .osu — different diffs in a set can chart at different grids).
 * Mapper credit is taken from the Normal bundle on the assumption
 * that all three diffs share the same set host (the importer
 * enforces that invariant).
 */
export function assembleImportedGuitarChart(
  args: AssembleImportedGuitarArgs,
): Record<Variant, GuitarChart> {
  const {
    baseSlug,
    baseTitle,
    description,
    difficulty,
    palette,
    audioSrc,
    attribution,
    imported,
  } = args;
  const out = {} as Record<Variant, GuitarChart>;
  for (const variant of VARIANTS) {
    const bundle = imported[variant];
    const tuning = GUITAR_VARIANT_TUNING[variant];
    const slug = buildVariantSlug(baseSlug, variant);
    const audio: ChartAudio = {
      src: audioSrc,
      bpm: bundle.bpm,
      offsetMs: bundle.offsetMs,
      durationSec: bundle.durationSec,
      attribution,
      // Mania charts don't ship per-track onset analysis (the source
      // mapper's note placement IS the onset map for our purposes —
      // every note lines up with something audible). Empty list is
      // legal: the chart validator only requires `audio.onsets` to
      // exist as an array.
      onsets: [],
    };
    const notes = withFillerGuitarNotes(
      buildNotesFromImported(bundle.notes),
      bundle.totalBeats,
    );
    out[variant] = {
      mode: 'guitar',
      id: slug,
      slug,
      title: buildVariantTitle(baseTitle, variant),
      description,
      bpm: bundle.bpm,
      difficulty,
      variant,
      totalBeats: bundle.totalBeats,
      palette,
      audio,
      notes,
      approachMs: tuning.approachMs,
      perfectWindowMs: PERFECT_WINDOW_MS * tuning.windowScale,
      goodWindowMs: GOOD_WINDOW_MS * tuning.windowScale,
      mapper: bundle.mapper,
    };
  }
  return out;
}
